import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetData,
  type SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetError,
  type SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetResponse,
  searchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGet,
} from '../../generated';

export type SearchIdeasQueryVariables = SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetData['path'] &
  SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetData['query'];

export type SearchIdeasQuery = {
  data: SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchIdeasQuery = <
  TData = SearchIdeasQuery,
  TError = SearchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGetError,
>(
  { workspace_id, image_id, limit, offset, sort_by, sort_direction }: SearchIdeasQueryVariables,
  options?: Omit<UseQueryOptions<SearchIdeasQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchIdeasQuery, TError, TData>({
    queryKey: ['SearchIdeas', { workspace_id, image_id, limit, offset, sort_by, sort_direction }],
    queryFn: async () => {
      const { data, response } = await searchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGet({
        path: { workspace_id, image_id },
        query: { limit, offset, sort_by, sort_direction },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchIdeasQuery.getKey = ({
  workspace_id,
  image_id,
  limit,
  offset,
  sort_by,
  sort_direction,
}: SearchIdeasQueryVariables) => ['SearchIdeas', { workspace_id, image_id, limit, offset, sort_by, sort_direction }];

useSearchIdeasQuery.fetcher = (
  { workspace_id, image_id, limit, offset, sort_by, sort_direction }: SearchIdeasQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchIdeasV1WorkspacesWorkspaceIdAssetsImagesImageIdIdeasGet({
      path: { workspace_id, image_id },
      query: { limit, offset, sort_by, sort_direction },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
